import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import HomeVisits from './services/HomeVisits';
import VideoConsultations from './services/VideoConsultations';
import FamilyPhysicians from './services/FamilyPhysicians';
import LabTests from './services/LabTests';
import SeniorCitizens from './services/SeniorCitizens';
import Vaccinations from './services/Vaccinations';

import Ultrasound from './services/Ultrasound';
import Echo2D from './services/Echo2D';
import DopplerScan from './services/DopplerScan';

import InnerHeroBanner from './InnerHeroBanner';
import { Helmet } from 'react-helmet-async';


/* =========================================================
   HEALTH PACKAGE DATA
   ========================================================= */

const healthPackages = [
  {
    id: 'basic',
    number: '01',
    name: 'Basic Health Panel',
    shortName: 'Basic',
    price: '₹2,799',
    description:
      'Essential health screening for routine preventive care and everyday health monitoring.',
    tag: 'Essential Care',
    services: [
      'Complete Blood Count',
      'Lipid Profile',
      'Thyroid Profile',
      'Liver Function Test',
      'Kidney Function Test',
      'Vitamin D',
      'Vitamin B12',
      'Total Calcium',
      'HbA1C',
      'Urine Analysis',
      'ECG',
      'Physician Consultation'
    ]
  },

  {
    id: 'executive',
    number: '02',
    name: 'Executive Health Panel',
    shortName: 'Executive',
    price: '₹5,999',
    description:
      'A comprehensive health assessment designed for regular preventive monitoring.',
    tag: 'Complete Care',
    services: [
      'Complete Blood Count',
      'HbA1C',
      'Thyroid Profile',
      'Total Calcium',
      'Vitamin B12',
      'Vitamin D',
      'Total Iron',
      'Liver Function Test',
      'Lipid Profile',
      'Urine Analysis',
      'FBS',
      'Kidney Function Test',
      'Ultrasound Abdomen',
      'Treadmill Stress Test (TMT)',
      'ECG',
      'Physician Consultation'
    ]
  },

  {
    id: 'master',
    number: '03',
    name: 'Master Health Panel',
    shortName: 'Master',
    price: '₹6,999',
    description:
      'Advanced preventive screening covering a wider range of health parameters and consultations.',
    tag: 'Advanced Care',
    services: [
      'Fasting Blood Sugar - FBS',
      'Complete Haemogram',
      'HbA1C',
      'Thyroid Profile',
      'Total Calcium',
      'Vitamin B12',
      'Vitamin D',
      'Total Iron',
      'Liver Function Test',
      'Lipid Profile',
      'Urine Analysis',
      'Kidney Function Test',
      'PSA (Male)',
      'Pap Smear (Female)',
      'CA-125',
      'ECG',
      'PFT',
      'Ultrasound Abdomen',
      'Treadmill Stress Test (TMT)',
      '2D Echo',
      'Physiotherapy Consultation',
      'Physician Consultation'
    ]
  },

  {
    id: 'women',
    number: '04',
    name: 'Women Wellness Panel',
    shortName: 'Women Wellness',
    price: '₹4,299',
    description:
      'Focused preventive screening designed specifically around women’s wellness.',
    tag: 'Women’s Care',
    services: [
      'Complete Blood Count',
      'Total Iron',
      'Thyroid Stimulating Hormone - TSH',
      'S. Prolactin',
      'FSH / LH',
      'S. Progesterone',
      'Estradiol',
      'Pap Smear',
      'Ultrasound Abdomen',
      'Physician Consultation'
    ]
  },

  {
    id: 'premium',
    number: '05',
    name: 'Premium Package',
    shortName: 'Premium',
    price: '₹9,999',
    description:
      'Our most comprehensive health assessment, combining advanced investigations with specialist consultations.',
    tag: 'Complete Wellness',
    premium: true,
    services: [
      'Fasting Blood Sugar - FBS',
      'Complete Haemogram',
      'HbA1C',
      'Thyroid Profile',
      'Total Calcium',
      'Vitamin B12',
      'Vitamin D',
      'Total Iron',
      'Liver Function Test',
      'Lipid Profile',
      'Urine Analysis',
      'S. Ferritin',
      'Kidney Function Test',
      'PSA (Male)',
      'Pap Smear (Female)',
      'CA-125',
      'ECG',
      'PFT',
      'Ultrasound Abdomen',
      'Treadmill Stress Test (TMT)',
      '2D Echo',
      'Physiotherapy Consultation',
      'Physician Consultation'
    ]
  }
];


/* =========================================================
   PACKAGE CARD
   ========================================================= */

function HealthPackageCard({ pkg }) {

  const [expanded, setExpanded] = useState(false);

  const visibleServices = expanded
    ? pkg.services
    : pkg.services.slice(0, 6);

  const remainingServices = pkg.services.length - 6;

  return (
    <article
      className={`health-package-card ${
        pkg.premium ? 'premium-package' : ''
      }`}
    >

      {/* Premium glow */}
      {pkg.premium && (
        <div className="health-package-glow"></div>
      )}


      {/* Card Header */}

      <div className="health-package-header">

        <div className="health-package-number">
          {pkg.number}
        </div>

        <div className="health-package-tag">
          {pkg.tag}
        </div>

      </div>


      {/* Package Identity */}

      <div className="health-package-identity">

        <div className="health-package-icon">
          <span>+</span>
        </div>

        <h3>{pkg.name}</h3>

        <p>
          {pkg.description}
        </p>

      </div>


      {/* Price */}

      <div className="health-package-price-area">

        <span className="health-package-price">
          {pkg.price}
        </span>

        <span className="health-package-price-note">
          / package
        </span>

      </div>


      {/* Divider */}

      <div className="health-package-divider"></div>


      {/* Includes */}

      <div className="health-package-includes">

        <div>
          <span className="includes-label">
            PACKAGE INCLUDES
          </span>

          <strong>
            {pkg.services.length} services
          </strong>
        </div>

        <div className="includes-dot"></div>

      </div>


      {/* Services */}

      <div
        className={`health-package-services ${
          expanded ? 'services-expanded' : ''
        }`}
      >

        {visibleServices.map((service, index) => (

          <div
            className="health-package-service"
            key={`${pkg.id}-${index}`}
          >

            <span className="service-index">
              {String(index + 1).padStart(2, '0')}
            </span>

            <span className="service-name">
              {service}
            </span>

          </div>

        ))}

      </div>


      {/* View More */}

      {pkg.services.length > 6 && (

        <button
          type="button"
          className="health-package-view-more"
          onClick={() => setExpanded(!expanded)}
        >

          <span>
            {expanded
              ? 'Show less'
              : `View all ${pkg.services.length} services`
            }
          </span>

          <span
            className={`view-more-arrow ${
              expanded ? 'arrow-up' : ''
            }`}
          >
            ↓
          </span>

        </button>

      )}


      {/* CTA */}

      <Link
        to="/contacts"
        className="health-package-btn"
      >

        <span>
          Enquire About This Package
        </span>

        <span className="package-arrow">
          →
        </span>

      </Link>

    </article>
  );
}


/* =========================================================
   HEALTH PACKAGES SECTION
   ========================================================= */

function HealthPackages() {

  return (

    <section className="health-packages-section">

      {/* Decorative background */}

      <div className="health-packages-bg-circle health-circle-one"></div>
      <div className="health-packages-bg-circle health-circle-two"></div>


      <div className="container">

        {/* =================================================
            SECTION INTRO
        ================================================= */}

        <div className="health-packages-heading">

          <div className="health-packages-eyebrow">

            <span className="eyebrow-line"></span>

            <span>
              PREVENTIVE HEALTHCARE
            </span>

            <span className="eyebrow-line"></span>

          </div>


          <h2>
            Health care that starts
            <br />
            <span>before you need it.</span>
          </h2>


          <p>
            Thoughtfully designed health screening packages to help you
            understand your health, identify potential concerns early,
            and stay one step ahead.
          </p>

        </div>


        {/* =================================================
            PACKAGE CARDS
        ================================================= */}

        <div className="health-packages-grid">

          {healthPackages.map((pkg) => (

            <HealthPackageCard
              key={pkg.id}
              pkg={pkg}
            />

          ))}

        </div>


        {/* =================================================
            BOTTOM NOTE
        ================================================= */}

        <div className="health-packages-footer">

          <div className="health-footer-icon">
            +
          </div>

          <div className="health-footer-content">

            <strong>
              Not sure which package is right for you?
            </strong>

            <span>
              Our team can help you choose a health check based on
              your age, lifestyle and preventive care needs.
            </span>

          </div>

          <Link
            to="/contacts"
            className="health-footer-link"
          >
            Talk to us
            <span>→</span>
          </Link>

        </div>

      </div>

    </section>
  );
}


/* =========================================================
   OUR SERVICES PAGE
   ========================================================= */

export default function OurServices() {

  return (
    <>

      <Helmet>

        <title>
          Our Services - Family Doctors at Home | FamPhy
        </title>

        <meta
          name="description"
          content="Explore FamPhy's healthcare services including doctor visits, video consultations, family physicians, laboratory tests, senior citizen care, vaccinations, ultrasound, 2D echo, Doppler scans and comprehensive health packages."
        />

        <meta
          name="keywords"
          content="FamPhy services, doctor home visit, video consultation, family physician, lab tests at home, senior citizen care, vaccinations, ultrasound, 2D echo, Doppler scan, health packages"
        />

      </Helmet>


      {/* =================================================
          INNER HERO
      ================================================= */}

      <InnerHeroBanner />


      {/* =================================================
          EXISTING SERVICES
      ================================================= */}

      <div className="our-services">

        <HomeVisits />

        <VideoConsultations />

        <FamilyPhysicians />

        <LabTests />

        <SeniorCitizens />

        <Vaccinations />

        <Ultrasound />

        <Echo2D />

        <DopplerScan />


      </div>


      {/* =================================================
          HEALTH PACKAGES
      ================================================= */}

      <HealthPackages />

    </>
  );
}